import { Box, Button, Container, Divider, Stack, TableCell, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { uniqBy, map as lodashMap } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { casesApi } from 'src/api/casesApi';
import { Filters } from 'src/components/Filters';
import SubHeader from 'src/components/SubHeader';
// import { Header } from 'src/components/Header';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { BillingType } from 'src/components/tables/renderers/BillingType';
import useGenericStyles from 'src/styles/useGenericStyles';
import capitalize from 'src/utils/capitalize';
import convertSecondsToTimeString from 'src/utils/convertSecondsToTimeString';
import setTitle from 'src/utils/setTitle';

function ViewPlanning() {
    setTitle('ITSM | Planning');
    const theme = useTheme();
    // states
    const [billingAccounts, setBillingAccounts] = useState([]);
    const [sprint, setSprint] = useState<any>({});
    const [sprints, setSprints] = useState<any>([]);
    const [allSprints, setAllSprints] = useState<any>([]);
    const [allData, setAllData] = useState<any>([]);
    const [page, setPage] = useState<any>(0);
    const [pageSize, setPageSize] = useState<any>(1000);
    const handlePage = (temp: number) => {
        setPage(temp);
    };
    const [totalCount, setTotalCount] = useState<number>(0);
    const handlePageSize = (temp: number) => {
        setPageSize(temp);
    };
    const [fetchingData, setFetchingData] = useState(false);
    const [filters, setFilters] = useState<any>({});
    const [sort, setSort] = useState<any>({});
    const [isExporting, setIsExporting] = useState(false);

    // hooks
    const classes = useGenericStyles();

    const getSprintIdsWithSameName = (_selectedSprint, _allSprints) => {
        console.log('selectedSprint', _selectedSprint);
        console.log('allSprints', _allSprints);
        if (!_selectedSprint || !_allSprints) return _selectedSprint?.id;

        const sameSprints = _allSprints.filter(
            (filterSprint) => filterSprint.name === _selectedSprint.name
        );

        console.log('sameSprints', sameSprints);

        return sameSprints.length > 0
            ? sameSprints.map((mapSprint) => mapSprint.id).join(',')
            : _selectedSprint?.id;
    };

    const getAllBillingAccounts = async () => {
        try {
            const response = await casesApi.getAllBillingAccounts();
            if (response.status === 200) {
                setBillingAccounts(response.data);
            }
        } catch (error) {
            if (error.message) {
                toast.error(error.message);
            } else {
                toast.error('An error occured while fetching types');
            }
        }
    };

    const getSprintsByOrganization = async () => {
        try {
            const organizationId = localStorage.getItem('organizationId');
            if (!organizationId) {
                setAllSprints([]);
                setSprints([]);
                return;
            }
            let response = await casesApi.getFutureSprintsByOrganization(organizationId);
            if (response.status === 200) {
                response = response?.data;
                // Add organizationId to each sprint object
                const sprintsWithOrgId = response.map((data: any) => ({
                    ...data,
                    organizationId
                }));
                setAllSprints(sprintsWithOrgId);
                const uniqueSprintNames = uniqBy(
                    sprintsWithOrgId,
                    (item: any) => item?.name
                );
                if (uniqueSprintNames?.length > 0) {
                    setSprint(uniqueSprintNames[0]);
                }
                setSprints(uniqueSprintNames);
            }
        } catch (error) {
            setAllSprints([]);
            setSprints([]);
        }
    };

    const handleSprintChange = (e) => {
        const res = sprints?.filter((item) => item?.name === e.target.value);
        if (res?.length > 0) {
            setSprint(res[0]);
        }
    };

    const getAllData = async () => {
        if (billingAccounts?.length > 0) {
            setFetchingData(true);
            try {
                const objs = {
                    page,
                    pageSize,
                    billingAccountId: lodashMap(billingAccounts, 'id')?.join(','),
                    to: sprint?.endDate?.substring(0, 10),
                    from: sprint?.startDate?.substring(0, 10),
                    sprintId: getSprintIdsWithSameName(sprint, allSprints),
                    organizationId: sprint?.organizationId,
                    column: sort?.field,
                    sort: sort?.order === 'asc' ? '1' : '-1',
                };
                const keys = Object.keys(objs);
                const values = Object.values(objs);

                // Create an array to store the non-empty key-value pairs
                const queryParams = [];
                for (let i = 0; i < values.length; i++) {
                    if (
                        values[i] !== '' &&
                        values[i] !== null &&
                        values[i] !== undefined
                    ) {
                        queryParams.push(`${encodeURIComponent(keys[i])}=${values[i]}`);
                    }
                }
                const queryString = queryParams.join('&');
                let response = await casesApi.getBillableStatisticsPlanning(queryString);
                if (response?.status === 200) {
                    response = response?.data;
                    setAllData(response?.data);
                    setTotalCount(response?.totalRecords);
                }
                if (response.status === 204) {
                    setAllData([]);
                    setTotalCount(0);
                }

                setFetchingData(false);
            } catch (error) {
                setFetchingData(false);
                if (error?.response?.status !== 401) {
                    toast.error(error?.response?.data?.message);
                    setAllData([]);
                    setTotalCount(0);
                }
            }
        }
    };

    const customColumns = [
        {
            field: 'project',
            headerName: 'Project',
            width: '10%',
            render: (columnValue, data) => (
                <Typography
                    title={capitalize(data?.project?.name)}
                    style={{
                        fontFamily: 'Inter',
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                    {capitalize(data?.project?.name)}
                </Typography>
            ),
            sortable: true,
        },
        {
            field: 'budget',
            headerName: 'Budget',
            width: '10%',
            render: (columnValue, data) => (
                <Typography
                    title={capitalize(data?.budget?.name)}
                    style={{
                        fontFamily: 'Inter',
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                    {capitalize(data?.budget?.name) || 'N/A'}
                </Typography>
            ),
            sortable: false,
        },
        {
            field: 'caseKey',
            headerName: 'Case Key',
            width: '9%',
            sortable: true,
        },
        {
            field: 'summary',
            headerName: 'Case Title',
            maxWidth: '400px',
            render: (columnValue, data) => (
                <Typography
                    title={capitalize(data?.summary)}
                    style={{
                        fontFamily: 'Inter',
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                    {console.log('summarydata', columnValue, data)}
                    {capitalize(data?.summary)}
                </Typography>
            ),
            sortable: true,
        },
        {
            field: 'timeEstimateBreakdown',
            headerName: 'Time Estimate',
            width: '10%',
            render: (_columnValue: any, data: any): any => {
                const { timeEstimateBreakdown } = data;
                const { billingAccountsPerCategory } = data;
                if (
                    !timeEstimateBreakdown ||
                    typeof timeEstimateBreakdown !== 'object'
                ) {
                    return <span>-</span>;
                }

                return (
                    <TableRow>
                        <TableCell>
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                        <th
                                            style={{
                                                padding: '8px 4px',
                                                textAlign: 'left',
                                                borderBottom: '1px solid #e0e0e0',
                                                borderRight: '1px solid #e0e0e0',
                                                fontSize: '10px',
                                            }}>
                                            Account
                                        </th>
                                        <th
                                            style={{
                                                padding: '8px 4px',
                                                textAlign: 'left',
                                                borderBottom: '1px solid #e0e0e0',
                                                borderRight: '1px solid #e0e0e0',
                                                fontSize: '10px',
                                            }}>
                                            Estimate
                                        </th>

                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(timeEstimateBreakdown).map((category) => {
                                        const billingAccountName =
                                            Array.isArray(billingAccountsPerCategory) &&
                                                billingAccountsPerCategory.length > 0
                                                ? billingAccountsPerCategory.find(
                                                    (item: any) => item.category === category,
                                                )?.name || ''
                                                : '';
                                        return (
                                            <tr
                                                key={category}
                                                style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td
                                                    style={{
                                                        padding: '6px 4px',
                                                        paddingRight: '20px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}>
                                                    <Tooltip title={billingAccountName}>
                                                        <Typography
                                                            style={{
                                                                fontSize: '12px',
                                                                maxWidth: '100px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                            {billingAccountName}
                                                        </Typography>
                                                    </Tooltip>
                                                </td>
                                                <td
                                                    style={{
                                                        padding: '6px 4px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}>
                                                    {convertSecondsToTimeString(
                                                        (
                                                            timeEstimateBreakdown as Record<
                                                                string,
                                                                number | undefined
                                                            >
                                                        )[category] ?? 0,
                                                    )}
                                                </td>

                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </TableCell>
                    </TableRow>
                );
            },
            sortable: true,
        },
        {
            field: 'category',
            headerName: 'Category',
            width: '5%',
            sortable: true,
            render: (columnValue) =>
                columnValue ? <BillingType value={columnValue} /> : 'N/A',
        },
        {
            field: 'sprint',
            headerName: 'Sprint',
            width: '5%',
            sortable: true,
            render: (columnValue) =>
                columnValue?.name?.length > 0 ? capitalize(columnValue?.name) : 'N/A',
        },
        {
            field: 'releaseVersion',
            headerName: 'Version',
            width: '5%',
            sortable: true,
            render: (columnValue) =>
                columnValue?.name?.length > 0 ? capitalize(columnValue?.name) : 'N/A',
        },
    ];

    const handleSort = (field) => {
        setSort((prevSort) => ({
            field,
            order:
                prevSort.field === field && prevSort.order === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleApplyFilter = (
        currentFilterColumn,
        filterValue,
        filterName,
        checked
    ) => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters };
            if (checked) {
                if (newFilters[currentFilterColumn]) {
                    newFilters[currentFilterColumn].push({
                        label: filterName,
                        value: filterValue,
                    });
                } else {
                    newFilters[currentFilterColumn] = [
                        {
                            label: filterName,
                            value: filterValue,
                        },
                    ];
                }
            } else {
                // eslint-disable-next-line no-lonely-if
                if (newFilters[currentFilterColumn]) {
                    newFilters[currentFilterColumn] = newFilters[
                        currentFilterColumn
                    ].filter((obj) => obj.value !== filterValue);

                    // If no filters are left for the column, remove the column from filters
                    if (newFilters[currentFilterColumn].length === 0) {
                        delete newFilters[currentFilterColumn];
                    }
                }
            }
            return newFilters;
        });
    };

    const handleRemoveFilter = (column) => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters };
            delete newFilters[column];
            return newFilters;
        });
    };

    useEffect(() => {
        if (sprints?.length === 0) {
            getSprintsByOrganization();
        }
        if (sprints?.length > 0) {
            getAllData();
        }
    }, [
        page,
        pageSize,
        JSON.stringify(filters),
        billingAccounts,
        sprint,
        sort
    ]);

    useEffect(() => {
        getAllBillingAccounts();
    }, []);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const objs = {
                billingAccountId: lodashMap(billingAccounts, 'id')?.join(','),
                to: sprint?.endDate?.substring(0, 10),
                from: sprint?.startDate?.substring(0, 10),
                sprintId: getSprintIdsWithSameName(sprint, allSprints),
                organizationId: sprint?.organizationId,
            };
            const keys = Object.keys(objs);
            const values = Object.values(objs);

            // Create an array to store the non-empty key-value pairs
            const queryParams = [];
            for (let i = 0; i < values.length; i++) {
                if (values[i] !== '' && values[i] !== null && values[i] !== undefined) {
                    queryParams.push(`${encodeURIComponent(keys[i])}=${values[i]}`);
                }
            }
            const queryString = queryParams.join('&');
            const response = await casesApi?.billableStatisticsExportPlanning(queryString);
            if (response?.status === 200) {
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Planning ${sprint?.name} ${moment(new Date()).format("YYYY-MM-DD")}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
            setIsExporting(false);
        } catch (error) {
            if (error?.response?.status !== 401) {
                toast.error('Please select a date range to proceed');
                setTotalCount(0);
            }

            setIsExporting(false);
        }
    };

    return (
        <>
            {/* <Header title="Planning" /> */}
            <SubHeader title='Planning' />
            <Container maxWidth={false} className={classes.container}>
                <Box
                    style={{
                        marginBottom: '10px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        divider={
                            <Divider
                                orientation="vertical"
                                flexItem
                                style={{
                                    height: '22px',
                                    alignSelf: 'center',
                                }}
                            />
                        }
                        alignItems="center">
                        <Box display="flex" alignItems="center" gap="10px">
                            <Button
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    gap: '5px',
                                    border: '1px solid transparent',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        backgroundColor: 'white',
                                    },
                                }}
                                disabled={isExporting}
                                className={classes.button}
                                onClick={handleExport}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    width="12"
                                    height="12">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span
                                    style={{
                                        marginLeft: '5px',
                                    }}>
                                    Export
                                </span>
                            </Button>
                        </Box>
                        <TextField
                            InputProps={{
                                classes: {
                                    root: classes.textField,
                                },
                            }}
                            sx={{
                                minWidth: '100px',
                                '& .MuiInputLabel-root': {
                                    fontSize: '13px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '13px',
                                },
                            }}
                            size="small"
                            fullWidth
                            label="Sprint"
                            placeholder="Sprint"
                            name="sprint"
                            required
                            onChange={handleSprintChange}
                            select
                            SelectProps={{ native: true }}
                            variant="outlined"
                            value={sprint?.name || ''}
                            defaultValue={sprint?.name || ''}>
                            {sprints?.map((item) => (
                                <option key={item?.name} value={item?.name}>
                                    {item?.name}
                                </option>
                            ))}
                        </TextField>
                    </Stack>
                </Box>
                {Object.keys(filters).length > 0 && (
                    <Filters filters={filters} handleRemoveFilter={handleRemoveFilter} />
                )}
                <CustomizedTable
                    heading="Planning Report"
                    loading={fetchingData}
                    columns={customColumns}
                    page={page}
                    limit={pageSize}
                    handleLimit={handlePageSize}
                    handlePage={handlePage}
                    data={allData}
                    totalRecords={totalCount}
                    handleSort={handleSort}
                    sort={sort}
                    handleApplyFilter={handleApplyFilter}
                    filters={filters}
                    navigateUrl="/cases"
                    navigateToKey
                    selectRowData
                />
            </Container>
        </>
    );
}

export default ViewPlanning;